import { Suspense, lazy, useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { useAuthStore } from './stores/authStore';
import AuthLayout from './layouts/AuthLayout';
import DashboardLayout from './layouts/DashboardLayout';
import LoadingScreen from './components/ui/LoadingScreen';

const isNativePlatform = Capacitor.isNativePlatform();

// Lazy loaded pages
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const SetupWizardPage = lazy(() => import('./pages/setup/SetupWizardPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const TasksPage = lazy(() => import('./pages/tasks/TasksPage'));
const TaskDetailsPage = lazy(() => import('./pages/tasks/TaskDetailsPage'));
const CreateTaskPage = lazy(() => import('./pages/tasks/CreateTaskPage'));
const EditTaskPage = lazy(() => import('./pages/tasks/EditTaskPage'));
const ImportTasksPage = lazy(() => import('./pages/tasks/ImportTasksPage'));
const CalendarPage = lazy(() => import('./pages/calendar/CalendarPage'));
const EmployeesPage = lazy(() => import('./pages/employees/EmployeesPage'));
const ClientsPage = lazy(() => import('./pages/clients/ClientsPage'));
const ClientDetailsPage = lazy(() => import('./pages/clients/ClientDetailsPage'));
const ImportClientsPage = lazy(() => import('./pages/clients/ImportClientsPage'));
const LiftsAdminPage = lazy(() => import('./pages/lifts/LiftsAdminPage'));
const LiftDetailsPage = lazy(() => import('./pages/lifts/LiftDetailsPage'));
const RealTimeMapPage = lazy(() => import('./pages/map/RealTimeMapPage'));
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));
const MasterControlPanel = lazy(() => import('./pages/master/MasterControlPanel'));
const FilesGalleryPage = lazy(() => import('./pages/files/FilesGalleryPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const MobileWorkerView = lazy(() => import('./pages/worker/MobileWorkerView'));
const CreateOnDutyTaskPage = lazy(() => import('./pages/worker/CreateOnDutyTaskPage'));
const MobileAdminView = lazy(() => import('./pages/admin/MobileAdminView'));
const UpdatePage = lazy(() => import('./pages/update/UpdatePage'));

function App() {
  const { isAuthenticated, user, checkAuth, hasPermission, hasFeature } = useAuthStore();
  const [needsSetup, setNeedsSetup] = useState<boolean | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState<{ version: string; downloadUrl: string; releaseNotes: string; forceUpdate: boolean } | null>(null);

  // Check authentication on app start
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Check for app updates on native platform
  useEffect(() => {
    if (!isNativePlatform) return;
    const checkUpdate = async () => {
      try {
        const appInfo = await CapApp.getInfo();
        const apiBase = import.meta.env.VITE_API_BASE_URL || '/api';
        const res = await fetch(`${apiBase}/app-version`);
        const data = await res.json();
        if (data.version && data.version !== appInfo.version) {
          setUpdateAvailable({
            version: data.version,
            downloadUrl: `${import.meta.env.VITE_API_BASE_URL || ''}${data.downloadUrl}`,
            releaseNotes: data.releaseNotes || '',
            forceUpdate: data.forceUpdate || false
          });
        }
      } catch (e) {
        // silently fail — don't block app usage
      }
    };
    checkUpdate();
  }, []);

  // Check if initial setup is needed
  useEffect(() => {
    // If setup was just completed in this session, skip the check
    if (localStorage.getItem('setup_completed') === '1') {
      setNeedsSetup(false);
      return;
    }
    const apiBase = import.meta.env.VITE_API_BASE_URL || '/api';
    fetch(`${apiBase}/setup/status`)
      .then(r => r.json())
      .then(data => setNeedsSetup(data.needsSetup === true))
      .catch(() => setNeedsSetup(false));
  }, []);

  // Na native platformi uvek ide na odgovarajuci mobile view
  const getMobileRoute = () => {
    if (!user) return '/login';
    return (user.role === 'admin' || user.role === 'master') ? '/admin/mobile' : '/worker/mobile';
  };

  // Route protection based on permissions
  const requirePermission = (permission: string) => (element: JSX.Element) => {
    if (isNativePlatform) return <Navigate to={getMobileRoute()} replace />;
    return user && hasPermission(permission) ? element : <Navigate to="/dashboard" replace />;
  };

  // Route protection based on roles
  const requireRole = (requiredRole: string) => (element: JSX.Element) => {
    if (isNativePlatform) return <Navigate to={getMobileRoute()} replace />;
    return user && user.role === requiredRole ? element : <Navigate to="/dashboard" replace />;
  };

  if (needsSetup === null) return <LoadingScreen />;

  return (
    <Suspense fallback={<LoadingScreen />}>
      {/* Update Modal for native app */}
      {updateAvailable && (
        <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl">
            <div className="text-center mb-4">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-100 rounded-full mb-3">
                <svg className="w-7 h-7 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900">Nova verzija dostupna!</h2>
              <p className="text-sm text-gray-500 mt-1">v{updateAvailable.version}</p>
            </div>
            {updateAvailable.releaseNotes && (
              <p className="text-sm text-gray-600 text-center mb-5">{updateAvailable.releaseNotes}</p>
            )}
            <a
              href={updateAvailable.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-xl transition-colors"
            >
              Ažuriraj aplikaciju
            </a>
            {!updateAvailable.forceUpdate && (
              <button
                onClick={() => setUpdateAvailable(null)}
                className="block w-full text-center text-gray-500 hover:text-gray-700 text-sm mt-3 py-2"
              >
                Kasnije
              </button>
            )}
          </div>
        </div>
      )}
      <Routes>
        {/* Public update/download page */}
        <Route path="/update" element={<UpdatePage />} />

        {/* Setup wizard — shown when no accounts exist yet */}
        <Route path="/setup" element={needsSetup ? <SetupWizardPage /> : <Navigate to="/login" replace />} />

        {/* Root: authenticated users go to dashboard or mobile, others to login */}
        <Route 
          path="/" 
          element={
            needsSetup
              ? <Navigate to="/setup" replace />
              : isAuthenticated 
                ? <Navigate to={isNativePlatform ? getMobileRoute() : '/dashboard'} replace /> 
                : <Navigate to="/login" replace />
          } 
        />

        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route 
            path="/login" 
            element={
              needsSetup
                ? <Navigate to="/setup" replace />
                : !isAuthenticated || user?.role === 'radnik' 
                ? <LoginPage /> 
                : <Navigate to="/dashboard" replace />
            } 
          />
        </Route>

        {/* Worker Mobile View - Without Dashboard Layout */}
        <Route 
          path="/worker/mobile" 
          element={isAuthenticated ? <MobileWorkerView /> : <Navigate to="/login" replace />} 
        />

        {/* Worker Create On-Duty Task - Separate page */}
        <Route 
          path="/worker/create-on-duty-task" 
          element={isAuthenticated ? <CreateOnDutyTaskPage /> : <Navigate to="/login" replace />} 
        />

        {/* Admin Mobile View - Without Dashboard Layout, only for admin role */}
        <Route 
          path="/admin/mobile" 
          element={isAuthenticated && (user?.role === 'admin' || user?.role === 'master') ? <MobileAdminView /> : <Navigate to="/login" replace />} 
        />

        {/* Protected routes */}
        <Route 
          element={
            isAuthenticated ? <DashboardLayout /> : <Navigate to="/login" replace />
          }
        >
          <Route 
            path="/dashboard" 
            element={isNativePlatform ? <Navigate to={getMobileRoute()} replace /> : <DashboardPage />} 
          />
          
          {/* Master Control Panel - Only for master role */}
          <Route path="/master" element={requireRole('master')(<MasterControlPanel />)} />
          
          {/* Tasks routes - accessible to all authenticated users */}
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/tasks/new" element={requirePermission('create_task')(<CreateTaskPage />)} />
          <Route path="/tasks/import" element={requirePermission('create_task')(<ImportTasksPage />)} />
          <Route path="/tasks/:taskId" element={<TaskDetailsPage />} />
          <Route path="/tasks/:taskId/edit" element={requirePermission('edit_task')(<EditTaskPage />)} />
          
          {/* Calendar route */}
          <Route path="/calendar" element={<CalendarPage />} />
          
          {/* Employees routes */}
          <Route path="/employees" element={requirePermission('view_employees')(<EmployeesPage />)} />
          
          {/* Clients (Stambene zajednice) routes */}
          <Route path="/clients" element={<ClientsPage />} />
          <Route path="/clients/import" element={<ImportClientsPage />} />
          <Route path="/clients/:id" element={<ClientDetailsPage />} />
          
          {/* Map route */}
          <Route path="/map" element={<RealTimeMapPage />} />
          <Route path="/gallery" element={requirePermission('manage_employees')(<FilesGalleryPage />)} />
          
          <Route path="/profile" element={<ProfilePage />} />
        </Route>

        {/* 404 Route */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default App;